import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Paper,
  Popover,
  Card,
  CardContent,
} from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getRemainingUnits } from "../../utils/apiClient";
import { useTheme } from "../../ThemeContext";
import BillingService from "./billing-service";


// Usage type color mapping and labels
const usageTypeConfig = {
  'voice_call': {
    color: '#FE4E3A', // Primary Orange/Red
    label: 'Voice Agent',
    itemLabel: 'minutes',
  },
  'fax_page': {
    color: '#FF9966', // Medium Orange
    label: 'Fax Agent - Pages',
    itemLabel: 'pages sent',
  },
  'fax_number_rental': {
    color: '#FFCC99', // Light Peach/Orange
    label: 'Fax Agent - Numbers',
    itemLabel: 'numbers purchased',
  },
};

const Usage = ({ units, setBillingData, maxWidth = 1800 }) => {
  const { themeMode } = useTheme();
  const [refreshing, setRefreshing] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(true);
  const [billingCycleSummary, setBillingCycleSummary] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState({ startDate: null, endDate: null });
  const [anchorEl, setAnchorEl] = useState(null);

  const percent = units.total > 0 ? Math.min(100, (units.used / units.total) * 100) : 0;
  const textColor = themeMode === "dark" ? "#ffffff" : "#000000";
  const borderColor = themeMode === "dark" ? "#3f3c57" : "#e2e2e2";
  const cardBg = themeMode === "dark" ? "#04002b" : "#f7f7f7";

  // Overage calculation
  const overageUnits = Math.max(0, (units.totalUsage ?? units.used) - (units.includedUnitsLimit ?? units.total));
  const showOverageCard = units.isOverageActive && units.planType && units.overagePlanType && units.planType !== units.overagePlanType;

  // When plan types differ, progress bar should show included limit (3/3) not total usage
  const isOverageMode = showOverageCard;
  const progressUsed = isOverageMode ? units.includedUnitsLimit : units.used;
  const progressTotal = isOverageMode ? units.includedUnitsLimit : units.total;
  const progressPercent = progressTotal > 0 ? Math.min(100, (progressUsed / progressTotal) * 100) : 0;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [unitsData, summaryData] = await Promise.all([
        getRemainingUnits(),
        BillingService.getCurrentBillingCycleSummary(),
      ]);
      
          if (setBillingData) {
        setBillingData((prev) => ({
          ...prev,
          units: {
            ...prev.units,
            used: unitsData.totalUsage ?? unitsData.total_usage ?? unitsData.unitsUsed ?? 0,
            total:
              unitsData.includedUnitsLimit ??
              unitsData.included_units_limit ??
              unitsData.unitsTotal ??
              0,
            isOverageActive: unitsData.isOverageActive ?? unitsData.is_overage_active ?? false,
            overagePlanType: unitsData.overagePlanType ?? null,
            planType: unitsData.planType ?? null,
            totalUsage: unitsData.totalUsage ?? unitsData.total_usage ?? 0,
            includedUnitsLimit: unitsData.includedUnitsLimit ?? unitsData.included_units_limit ?? 0,
            overageRatePerHour: unitsData.overageRatePerHour ?? unitsData.overage_rate_per_hour ?? null,
          },
        }));
      }
      
      if (summaryData.success) {
        setBillingCycleSummary(summaryData);
        if (summaryData.startDate && summaryData.endDate) {
          setBillingPeriod({
            startDate: summaryData.startDate,
            endDate: summaryData.endDate,
          });
        }
      }
      
      // Also refresh chart data
      await fetchUsageRecords();
    } catch (e) {
      // Optionally show error
    } finally {
      setRefreshing(false);
    }
  };

  // Format date as "Nov 1", "Nov 2", etc.
  const formatDateLabel = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };
  
  // Format billing period date range for subtitle
  const formatBillingPeriodSubtitle = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Daily usage for current billing period';
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Daily usage for current billing period';
    }
    
    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const endDay = end.getDate();
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    
    if (startMonth === endMonth && startYear === endYear) {
      return `Daily usage for current billing period (${startMonth} ${startDay}-${endDay}, ${startYear})`;
    } else if (startYear === endYear) {
      return `Daily usage for current billing period (${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear})`;
    }
    return `Daily usage for current billing period (${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear})`;
  };

  // Convert UTC date to local date string (YYYY-MM-DD)
  const convertUTCToLocalDate = (utcDateString) => {
    const date = new Date(utcDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Custom tooltip component for the chart - needs access to dailyData
  const CustomTooltip = ({ point }) => {
    const date = point.data.x;
    
    // Find values for all three series for this date
    let voiceValue = 0;
    let faxPagesValue = 0;
    let faxNumbersValue = 0;
    
    if (dailyData && dailyData.length > 0) {
      dailyData.forEach((series) => {
        const dataPoint = series.data.find((d) => d.x === date);
        if (dataPoint) {
          if (series.id === 'Voice Agent') {
            voiceValue = dataPoint.y;
          } else if (series.id === 'Fax Agent - Pages') {
            faxPagesValue = dataPoint.y;
          } else if (series.id === 'Fax Agent - Numbers') {
            faxNumbersValue = dataPoint.y;
          }
        }
      });
    }
    
    return (
      <Box
        sx={{
          background: '#ffffff',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          minWidth: '240px',
          position: 'relative',
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#000000',
            mb: 2,
          }}
        >
          {date}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>
              {usageTypeConfig.voice_call.label}:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: usageTypeConfig.voice_call.color }}>
              {voiceValue.toLocaleString()} units
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>
              {usageTypeConfig.fax_page.label}:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: usageTypeConfig.fax_page.color }}>
              {faxPagesValue.toLocaleString()} units
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>
              {usageTypeConfig.fax_number_rental.label}:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: usageTypeConfig.fax_number_rental.color }}>
              {faxNumbersValue.toLocaleString()} units
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // Process usage records and group by date and usage type
  const processUsageRecords = useCallback((records) => {
    if (!records || records.length === 0) {
      return { chartData: [], dateLabels: [] };
    }

    // Group records by date (local timezone) and usage type
    const dailyUsage = {};
    const dateSet = new Set();

    records.forEach((record) => {
      const localDate = convertUTCToLocalDate(record.createdAt);
      dateSet.add(localDate);
      
      if (!dailyUsage[localDate]) {
        dailyUsage[localDate] = {
          voice_call: 0,
          fax_page: 0,
          fax_number_rental: 0,
        };
      }
      
      if (dailyUsage[localDate][record.usageType] !== undefined) {
        dailyUsage[localDate][record.usageType] += record.unitsDeducted || 0;
      }
    });

    // Sort dates
    const sortedDates = Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));

    // Generate date labels
    const dateLabels = sortedDates.map(formatDateLabel);

    // Prepare chart data for each usage type
    const chartData = [
      {
        id: 'Fax Agent - Numbers',
        color: usageTypeConfig.fax_number_rental.color,
        data: sortedDates.map((date, index) => ({
          x: dateLabels[index],
          y: dailyUsage[date]?.fax_number_rental || 0,
        })),
      },
      {
        id: 'Fax Agent - Pages',
        color: usageTypeConfig.fax_page.color,
        data: sortedDates.map((date, index) => ({
          x: dateLabels[index],
          y: dailyUsage[date]?.fax_page || 0,
        })),
      },
      {
        id: 'Voice Agent',
        color: usageTypeConfig.voice_call.color,
        data: sortedDates.map((date, index) => ({
          x: dateLabels[index],
          y: dailyUsage[date]?.voice_call || 0,
        })),
      },
    ];

    return { chartData, dateLabels };
  }, []);

  // Fetch usage records
  const fetchUsageRecords = async () => {
    setLoadingChart(true);
    try {
      const result = await BillingService.getUsageRecords();
      if (result.success && result.records) {
        const { chartData } = processUsageRecords(result.records);
        setDailyData(chartData);
      } else {
        setDailyData([]);
      }
    } catch (error) {
      console.error('Error fetching usage records:', error);
      setDailyData([]);
    } finally {
      setLoadingChart(false);
    }
  };

  // Fetch current billing cycle summary and usage records on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryResult, recordsResult] = await Promise.all([
          BillingService.getCurrentBillingCycleSummary(),
          BillingService.getUsageRecords(),
        ]);
        
        if (summaryResult.success) {
          setBillingCycleSummary(summaryResult);
          if (summaryResult.startDate && summaryResult.endDate) {
            setBillingPeriod({
              startDate: summaryResult.startDate,
              endDate: summaryResult.endDate,
            });
          }
        }
        
        if (recordsResult.success && recordsResult.records) {
          const { chartData } = processUsageRecords(recordsResult.records);
          setDailyData(chartData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingChart(false);
      }
    };
    fetchData();
  }, [processUsageRecords]);

  // Calculate segments for the progress bar based on usage types
  const getProgressBarSegments = () => {
    if (!billingCycleSummary || !billingCycleSummary.summaryByType || units.used === 0 || units.total === 0) {
      return [];
    }

    const segments = [];
    let currentPosition = 0;
    const totalUsed = units.used;

    // Order: voice_call (red), fax_page (orange-red), fax_number_rental (orange)
    const orderedTypes = ['voice_call', 'fax_page', 'fax_number_rental'];
    
    orderedTypes.forEach(type => {
      const summary = billingCycleSummary.summaryByType.find(s => s.usageType === type);
      if (summary && summary.totalUnits > 0 && totalUsed > 0) {
        // Calculate percentage based on used units, not total units
        const segmentPercent = (summary.totalUnits / totalUsed) * percent;
        const displayPercent = Math.min(segmentPercent, percent - currentPosition);
        
        if (displayPercent > 0.1) { // Only show if significant (> 0.1%)
          const config = usageTypeConfig[type] || { color: '#FF5C5C', label: type, itemLabel: 'items' };
          segments.push({
            type,
            color: config.color,
            width: displayPercent,
            left: currentPosition,
            units: summary.totalUnits,
            totalItems: summary.totalItems || 0,
            label: config.label,
            itemLabel: config.itemLabel,
          });
          currentPosition += displayPercent;
        }
      }
    });

    return segments;
  };


  // Inject styles for tooltip arrows to match segment colors
  useEffect(() => {
    if (!billingCycleSummary || !billingCycleSummary.summaryByType || units.used === 0 || units.total === 0) {
      return;
    }

    const segments = [];
    let currentPosition = 0;
    const totalUsed = units.used;
    const percent = units.total > 0 ? Math.min(100, (units.used / units.total) * 100) : 0;
    const orderedTypes = ['voice_call', 'fax_page', 'fax_number_rental'];
    
    orderedTypes.forEach(type => {
      const summary = billingCycleSummary.summaryByType.find(s => s.usageType === type);
      if (summary && summary.totalUnits > 0 && totalUsed > 0) {
        const segmentPercent = (summary.totalUnits / totalUsed) * percent;
        const displayPercent = Math.min(segmentPercent, percent - currentPosition);
        
        if (displayPercent > 0.1) {
          const config = usageTypeConfig[type] || { color: '#FF5C5C', label: type, itemLabel: 'items' };
          segments.push({
            type,
            color: config.color,
            width: displayPercent,
            left: currentPosition,
            units: summary.totalUnits,
            totalItems: summary.totalItems || 0,
            label: config.label,
            itemLabel: config.itemLabel,
          });
          currentPosition += displayPercent;
        }
      }
    });

    const styleId = 'tooltip-arrow-styles';
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    const styles = segments.map((segment, index) => {
      const tooltipSelector = `[data-tooltip-segment="${segment.type}-${index}"]`;
      return `
        ${tooltipSelector} .MuiTooltip-tooltip::after {
          content: "" !important;
          position: absolute !important;
          bottom: -6px !important;
          left: 50% !important;
          transform: translateX(-50%) rotate(45deg) !important;
          width: 12px !important;
          height: 12px !important;
          backgroundColor: ${segment.color} !important;
          boxShadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        }
      `;
    }).join('');

    styleElement.textContent = styles;

    return () => {
      // Cleanup on unmount
      const element = document.getElementById(styleId);
      if (element) {
        element.remove();
      }
    };
  }, [billingCycleSummary, units]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        maxWidth: maxWidth,
        mx: "auto",
        width: "100%",
      }}
    >
      {/* Units Used Section */}
      <Paper
        sx={{
          borderRadius: 2,
          px: 3,
          py: 2,
          background: cardBg,
          color: textColor,
          border: `1px solid ${borderColor}`,
          boxShadow: "none",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
        >
          <Box display="flex" alignItems="center">
            <Typography variant="h5" sx={{ color: textColor, fontWeight: 600, fontSize: "1.25rem" }}>
              Units Used
            </Typography>
            <Box
              onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
              onMouseLeave={() => setAnchorEl(null)}
              sx={{ ml: 1, p: 0.5, display: 'inline-flex', cursor: "help" }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 20, color: textColor }} />
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <Typography variant="h5" sx={{ color: textColor, mr: 1.5, fontWeight: 600, fontSize: "1.25rem" }}>
              {progressUsed.toLocaleString()} / {progressTotal.toLocaleString()}
            </Typography>
            <Tooltip title="Refresh units">
              <span>
                <IconButton
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{ color: textColor }}
                >
                  {refreshing ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RefreshIcon fontSize="medium" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box
          sx={{
            position: 'relative',
            height: 10,
            borderRadius: 4,
            backgroundColor: themeMode === "dark" ? "#3f3c57" : "#e2e2e2",
            overflow: 'visible',
          }}
        >
          {getProgressBarSegments().map((segment, index) => {
            const segments = getProgressBarSegments();
            const isFirst = index === 0 && segment.left === 0;
            const isLast = index === segments.length - 1 && segment.left + segment.width >= percent;
            
            return (
              <Tooltip
                key={segment.type}
                data-tooltip-segment={`${segment.type}-${index}`}
                title={
                  <Box sx={{ p: 0 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1, 
                        color: '#000000',
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {segment.label}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 1, 
                        color: '#000000',
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {segment.totalItems.toLocaleString()} {segment.itemLabel}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#000000',
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {segment.units.toLocaleString()} units
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    'data-tooltip-segment': `${segment.type}-${index}`,
                    sx: {
                      bgcolor: '#ffffff',
                      color: '#000000',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      maxWidth: 'none',
                      fontSize: '0.875rem',
                      position: 'relative',
                      '& .MuiTooltip-arrow': {
                        display: 'none', // Hide default arrow
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-6px',
                        left: '50%',
                        transform: 'translateX(-50%) rotate(45deg)',
                        width: '12px',
                        height: '12px',
                        backgroundColor: segment.color,
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      },
                    },
                  },
                  arrow: false, // Disable default arrow
                }}
                PopperProps={{
                  'data-tooltip-segment': `${segment.type}-${index}`,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${segment.left}%`,
                    width: `${segment.width}%`,
                    height: '100%',
                    backgroundColor: segment.color,
                    borderRadius: isFirst ? '4px 0 0 4px' : isLast ? '0 4px 4px 0' : '0',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.9,
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        {/* <Typography
          variant="body1"
          sx={{
            color: textColor,
            mt: 1,
            opacity: 0.8,
            fontSize: "0.875rem",
          }}
        >
          {percent.toFixed(1)}% of your monthly limit used
        </Typography> */}
      </Paper>

      {/* Overage Warning Card */}
      {showOverageCard && (
        <Card
          sx={{
            borderRadius: 2,
            px: 3,
            py: 2,
            background: cardBg,
            border: `1px solid ${borderColor}`,
            maxWidth: maxWidth,
            margin: "0 auto",
            marginBottom: 4,
          }}
        >
          <CardContent sx={{ p: "0 !important", display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon sx={{ color: themeMode === "dark" ? "#ff6b6b" : "#d32f2f", fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: themeMode === "dark" ? "#ff6b6b" : "#c62828" }}>
              <strong>Overage:</strong> {overageUnits} units used beyond included limit. You will be charged at ${units.overageRatePerHour}/hour in your next invoice.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Unit Consumption Chart Section */}
      <Paper
        sx={{
          display: "none",
          borderRadius: 2,
          p: 2.5,
          background: cardBg,
          color: textColor,
          border: `1px solid ${borderColor}`,
          boxShadow: "none",
        }}
      >
        <Box mb={2}>
          <Typography variant="h5" sx={{ color: textColor, fontWeight: 600, mb: 0.5, fontSize: "1.25rem" }}>
            Unit Consumption
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: textColor,
              opacity: 0.7,
              fontSize: "0.875rem",
            }}
          >
            {formatBillingPeriodSubtitle(billingPeriod.startDate, billingPeriod.endDate)}
          </Typography>
        </Box>
        {loadingChart ? (
          <Box
            sx={{
              height: "320px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : dailyData && dailyData.length > 0 ? (
          <Box sx={{ height: "320px", width: "100%" }}>
            <ResponsiveLine
              data={dailyData}
              margin={{ top: 15, right: 100, bottom: 50, left: 100 }}
              xScale={{ type: "point" }}
              yScale={{
                type: "linear",
                min: 0,
                max: "auto",
                stacked: false,
                reverse: false,
              }}
              curve="monotoneX"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                orient: "bottom",
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "",
                tickColor: textColor,
              }}
              axisLeft={{
                orient: "left",
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "Units",
                legendOffset: -45,
                legendPosition: "middle",
                tickColor: textColor,
              }}
              enableGridX={false}
              enableGridY={true}
              colors={(d) => d.color}
              lineWidth={2}
              pointSize={6}
              pointColor={{ theme: "background" }}
              pointBorderWidth={2}
              pointBorderColor={{ from: "serieColor" }}
              pointLabelYOffset={-12}
              useMesh={true}
              enableArea={false}
              animate={true}
              tooltip={CustomTooltip}
              theme={{
                axis: {
                  ticks: {
                    text: {
                      fill: textColor,
                      fontSize: 13,
                    },
                  },
                  legend: {
                    text: {
                      fill: textColor,
                      fontSize: 15,
                    },
                  },
                },
                grid: {
                  line: {
                    stroke: themeMode === "dark" ? "#3f3c57" : "#e2e2e2",
                    strokeWidth: 1,
                  },
                },
                legends: {
                  text: {
                    fill: textColor,
                  },
                },
              }}
              legends={[
                {
                  anchor: "bottom",
                  direction: "row",
                  justify: false,
                  translateX: 0,
                  translateY: 40,
                  itemsSpacing: 30,
                  itemDirection: "left-to-right",
                  itemWidth: 140,
                  itemHeight: 18,
                  itemOpacity: 1,
                  symbolSize: 12,
                  symbolShape: "circle",
                  symbolBorderColor: textColor,
                  textColor: textColor,
                  effects: [
                    {
                      on: "hover",
                      style: {
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]}
            />
          </Box>
        ) : (
          <Box
            sx={{
              height: "320px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body1" sx={{ color: textColor, opacity: 0.7, fontSize: "0.875rem" }}>
              No usage data available
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Unit Pricing Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
          '& .MuiPopover-paper': {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            maxWidth: '500px',
            ml: 1,
            pointerEvents: 'auto',
          },
        }}
        onMouseEnter={(e) => {
          // Keep popover open when hovering over it
          e.stopPropagation();
        }}
        onMouseLeave={() => setAnchorEl(null)}
      >
        <Box
          onMouseEnter={(e) => {
            // Keep popover open when hovering over content
            e.stopPropagation();
          }}
          sx={{
            p: 3,
            background: '#ffffff',
            color: '#000000',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: '1.5rem',
              color: '#000000',
              mb: 2,
            }}
          >
            Unit Pricing
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.875rem',
              color: '#000000',
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            Your plan includes a monthly allocation of units that are consumed across different services based on usage.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Voice Agent */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#000000',
                  }}
                >
                  Voice Agent:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#000000',
                  }}
                >
                  1 unit/min
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#666666',
                  lineHeight: 1.5,
                }}
              >
                Each minute of voice call consumes 1 unit
              </Typography>
            </Box>

            {/* Fax Agent (Page) */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#000000',
                  }}
                >
                  Fax Agent (Page):
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#000000',
                  }}
                >
                  10 units
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#666666',
                  lineHeight: 1.5,
                }}
              >
                Each fax page sent consumes 10 units
              </Typography>
            </Box>

            {/* Fax Agent (Number) */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#000000',
                  }}
                >
                  Fax Agent (Number):
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#000000',
                  }}
                >
                  1,000 units
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#666666',
                  lineHeight: 1.5,
                }}
              >
                Each fax number purchased consumes 1,000 units monthly
              </Typography>
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#666666',
              mt: 3,
              pt: 3,
              borderTop: '1px solid #e2e2e2',
              lineHeight: 1.5,
            }}
          >
            Hover over each segment in the progress bar to see detailed consumption breakdown.
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
};

export default Usage;

